import React, { useMemo } from 'react';
import {
    DataGridPro,
    DataGridProProps, GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import CustomGridToolbar from "../CustomGridToolbar";
import { gridAlignCentre } from "../../../util/grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GridColumnVisibilityModel } from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import { setColumnVisibility } from "../../../features/address/addressSlice";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { RootState } from "../../../util/store";
import { GridColumnDef, GridColumnTemplate } from "../grid";

export interface AddressBookGridProps extends Omit<DataGridProProps, 'columns' | 'checkboxSelection' | 'components'> {
    columnDef: GridColumnDef[];
}

export default function AddressBookGrid(props: AddressBookGridProps): JSX.Element {
    const {
        columnDef,
        ...rest
    } = props;

    const dispatch = useAppDispatch();
    const columnVisibility = useAppSelector((state: RootState) => state.address.columnVisibility);

    const columnTemplate:GridColumnTemplate = {
        id: {field: 'id', headerName: 'ID', width: 120, hide: true},
        edit: {
            field: 'edit',
            headerName: 'Edit',
            width: 50,
            disableColumnMenu: true,
            disableExport: true,
            disableReorder: true,
            filterable: false,
            resizable: false,
            sortable: false,
            align: gridAlignCentre,
            headerAlign: gridAlignCentre,
            renderHeader: () => (
                <FontAwesomeIcon size='lg' icon={['far', 'edit']} title='Edit address'/>
            ),
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.description === "Default Pick/Del Address") {
                    return null;
                }
                return (
                    <FontAwesomeIcon size='lg' icon={['far', 'edit']} title='Edit address'/>
                );
            },
        },
        remove: {
            field: 'remove',
            headerName: 'Remove',
            width: 50,
            disableColumnMenu: true,
            disableExport: true,
            disableReorder: true,
            filterable: false,
            resizable: false,
            sortable: false,
            align: gridAlignCentre,
            headerAlign: gridAlignCentre,
            renderHeader: () => (
                <FontAwesomeIcon size='lg' icon={['fas', 'trash']} title='Remove address'/>
            ),
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.description === "Default Pick/Del Address") {
                    return null;
                }
                return (
                    <FontAwesomeIcon size='lg' icon={['fas', 'trash']} title='Remove address'/>
                );
            },
        },
        description: {field: 'description', headerName: 'Description', width: 200},
        contact: {field: 'contact', headerName: 'Onsite Contact Name', width: 180},
        line1: {field: 'line1', headerName: 'Suite/Level/Building/Unit Number/Letter', width: 200},
        line2: {field: 'line2', headerName: 'Number, Street Name, Street Type', width: 200},
        line3: {field: 'line3', headerName: 'Suburb, State, Postcode', width: 200},
        phone: {field: 'phone', headerName: 'Contact Phone', width: 150},
    };

    const columns = useMemo(() => {
        const ret:GridColDef[] = [];
        columnDef.forEach((item) => {
            ret.push({
                ...columnTemplate[item.field],
                headerName: item.headerName !== undefined ? item.headerName : columnTemplate[item.field].headerName,
                width: item.width !== undefined ? item.width : columnTemplate[item.field].width,
            })
        });

        return ret;
    }, []);

    const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
        dispatch(setColumnVisibility({
            ...model,
            id: false,
            edit: true,
            remove: true,
        }));
    }

    return (
        <DataGridPro
            {...rest}
            disableColumnMenu
            disableSelectionOnClick
            disableMultipleSelection
            columns={columns}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            components={{
                Toolbar: CustomGridToolbar,
            }}
            localeText={{
                toolbarExport: 'Export grid results',
                toolbarExportLabel: 'Export grid results',
            }}
        />
    );
}
